import { plateFont } from '../../../utils/fonts';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import InputAdornment from '@mui/material/InputAdornment';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import { useNavigate } from 'react-router-dom';
import { calcPriceInclVat, formatPriceWithCurrency } from '../../../utils/vat';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useUserStore } from '../../../store';
import { OemPackageCardRecommended, OemPackageCardSmall } from './OemPackageCard';
import OemPreviewPane from './OemPreviewPane';
import LisapalveluSection from './LisapalveluSection';
import PartSwapModal from './PartSwapModal';
import OemJobSetupDialog from './OemJobSetupDialog';

export default function OemServiceDialog({
  open,
  handleClose,
  mode = 'create', // 'create' | 'augment' | 'picker'
  vehicleId: propVehicleId,
  vehicleData: propVehicle,
  initialMileage,
  initialCustomerId,
  addCreatedMaintenance,
  maintenanceId, // augment mode: the work order to attach the package to
  onPackageSelected, // picker mode: receives the selection payload, no backend call
  onAttached, // augment mode: called after a successful attach (success feedback)
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;

  // Form state
  const [plate, setPlate] = useState('');
  const [mileage, setMileage] = useState('');
  const [vehicleId, setVehicleId] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState(null);

  // OEM state
  const [fetchStatus, setFetchStatus] = useState('idle'); // idle, loading, success, error
  const [fetchError, setFetchError] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [pickedServiceId, setPickedServiceId] = useState(null);
  const [packageDetail, setPackageDetail] = useState(null);
  const [packageLoading, setPackageLoading] = useState(false);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({}); // { partName: variant }
  const [selectedCatalogueItems, setSelectedCatalogueItems] = useState({}); // { partName: catalogue_item }
  const [lastUpdated, setLastUpdated] = useState(null);
  const [creating, setCreating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Customer override (from AddMaintenanceDialog transition)
  const [customerId, setCustomerId] = useState(null);

  // Swap modal
  const [swapPart, setSwapPart] = useState(null);
  const [swapOpen, setSwapOpen] = useState(false);

  // Job setup
  const [jobSetupOpen, setJobSetupOpen] = useState(false);
  const [defaultJobId, setDefaultJobId] = useState(currentOrganization?.default_oem_service_job);

  const expanded = pickedServiceId != null && packageDetail != null;

  // Pre-fill for augment mode or create mode with data from AddMaintenanceDialog
  useEffect(() => {
    if (!open) return;
    if ((mode === 'augment' || mode === 'create' || mode === 'picker') && propVehicle) {
      setPlate(propVehicle.registration_number || '');
      setVehicleId(propVehicleId || propVehicle.id || null);
      setVehicleInfo(propVehicle);
      if (initialMileage) setMileage(String(initialMileage));
    }
    if (initialCustomerId) setCustomerId(initialCustomerId);
  }, [open, mode, propVehicle, propVehicleId, initialMileage, initialCustomerId]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setPlate('');
      setMileage('');
      setVehicleId(null);
      setVehicleInfo(null);
      setFetchStatus('idle');
      setFetchError('');
      setPreviewData(null);
      setPickedServiceId(null);
      setPackageDetail(null);
      setSelectedExtras([]);
      setSelectedVariants({});
      setSelectedCatalogueItems({});
      setSwapPart(null);
      setSwapOpen(false);
      setCustomerId(null);
      autoFetchedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [open]);

  const pollIntervalRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  // × 2s interval = 6 min. Must exceed the Celery task's 330s hard limit — a slow
  // but successful (and paid) Ezyvin fetch must never be shown as a timeout.
  const MAX_POLL_ATTEMPTS = 180;

  const handlePreviewResult = useCallback((data) => {
    setPreviewData(data);
    setFetchStatus('success');
    setLastUpdated(0);

    if (data.vehicle) {
      setVehicleId(data.vehicle.id);
      setVehicleInfo(data.vehicle);
    }
    if (data.recommended) {
      setPickedServiceId(data.recommended.service_id);
      setPackageDetail(data.recommended);
    }
  }, []);

  const pollTaskStatus = useCallback((taskId) => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    pollAttemptsRef.current = 0;

    pollIntervalRef.current = setInterval(async () => {
      // Hard cap: a dead worker leaves the task PENDING forever — never poll past ~2 min.
      pollAttemptsRef.current += 1;
      if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setFetchError(t('oem.previewTimeout'));
        setFetchStatus('error');
        return;
      }
      try {
        const resp = await axios.get(`/api/oem/preview/status/${taskId}/`, {
          params: { organization: orgId },
        });

        if (resp.data.status === 'pending') return; // keep polling

        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;

        if (resp.data.error) {
          setFetchError(resp.data.error);
          setFetchStatus('error');
        } else {
          handlePreviewResult(resp.data);
        }
      } catch (err) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        setFetchError(err.response?.data?.error || t('oem.fetchError'));
        setFetchStatus('error');
      }
    }, 2000);
  }, [orgId, t, handlePreviewResult]);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, []);

  const fetchPreview = useCallback(async (plateOrVehicleId, km) => {
    if (!plateOrVehicleId || !km || parseInt(km) <= 0) return;

    setFetchStatus('loading');
    setFetchError('');
    setPreviewData(null);
    setPickedServiceId(null);
    setPackageDetail(null);

    try {
      const params = { mileage: parseInt(km), organization: orgId };
      if (typeof plateOrVehicleId === 'number' || (typeof plateOrVehicleId === 'string' && /^\d+$/.test(plateOrVehicleId))) {
        params.vehicle_id = plateOrVehicleId;
      } else {
        params.registration_number = plateOrVehicleId;
      }

      const resp = await axios.get('/api/oem/preview/', { params });

      if (resp.data.task_id) {
        // Async: poll for result
        pollTaskStatus(resp.data.task_id);
      } else if (resp.data.vehicle) {
        // Sync result (shouldn't happen now, but handle it)
        handlePreviewResult(resp.data);
      }
    } catch (err) {
      const msg = err.response?.data?.error || t('oem.fetchError');
      setFetchError(msg);
      setFetchStatus('error');
    }
  }, [orgId, t, pollTaskStatus, handlePreviewResult]);

  // Trigger fetch — uses plate directly (backend handles vehicle lookup/creation)
  const triggerFetch = useCallback(() => {
    const identifier = vehicleId || plate;
    if (!identifier || !mileage || parseInt(mileage) <= 0) return;
    if (!defaultJobId) {
      setJobSetupOpen(true);
      return;
    }
    fetchPreview(identifier, mileage);
  }, [vehicleId, plate, mileage, defaultJobId, fetchPreview]);

  // Auto-fetch once when vehicle is known and mileage is pre-filled
  const autoFetchedRef = useRef(false);
  useEffect(() => {
    if (autoFetchedRef.current) return;
    const hasMileage = mileage && parseInt(mileage) > 0;
    const shouldAutoFetch =
      (mode === 'augment' && vehicleId && hasMileage) ||
      ((mode === 'create' || mode === 'picker') && propVehicle && hasMileage);
    if (shouldAutoFetch) {
      autoFetchedRef.current = true;
      triggerFetch();
    }
  }, [mode, vehicleId, mileage, propVehicle, triggerFetch]);

  // Full preview reset: an edited plate or mileage makes everything derived from
  // the previous fetch stale — including the picked package and part choices. A
  // partial reset here once left the old vehicle's package on screen with an
  // enabled create button.
  const resetPreviewState = () => {
    setPreviewData(null);
    setFetchStatus('idle');
    setFetchError('');
    setPickedServiceId(null);
    setPackageDetail(null);
    setSelectedExtras([]);
    setSelectedVariants({});
    setSelectedCatalogueItems({});
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const handlePlateChange = (value) => {
    setPlate(value);
    if (mode !== 'augment') {
      setVehicleId(null);
      setVehicleInfo(null);
    }
    resetPreviewState();
  };

  const handleMileageChange = (value) => {
    setMileage(value.replace(/\D/g, ''));
    // Mileage drives the recommended interval — a fetched preview is stale the
    // moment it changes. Back to idle so the fetch button (and blur) re-fetch.
    if (fetchStatus !== 'idle') resetPreviewState();
  };

  const fetchPackageDetail = React.useCallback(async (serviceId, extras) => {
    setPackageLoading(true);
    try {
      // The odometer reading lets the backend place the service on the road (its
      // service point on a repeating cycle) and name it within its cycle.
      const params = { organization: orgId, mileage: parseInt(mileage) || 0 };
      if (extras?.length) params.extra_service_ids = extras.join(',');
      const resp = await axios.get(`/api/oem/preview/${vehicleId}/package/${serviceId}/`, {
        params,
      });
      setPackageDetail(resp.data);
      return true;
    } catch (err) {
      // Keep whatever was shown — a failed refresh must not blank the preview —
      // but tell the user and let callers revert their selection: a card selection
      // that diverges from the pane content lets the user confirm a package they
      // never reviewed.
      setSnackbar({
        open: true,
        message: err.response?.data?.error || t('oem.fetchError'),
        severity: 'error',
      });
      return false;
    } finally {
      setPackageLoading(false);
    }
  }, [orgId, vehicleId, mileage, t]);

  // Click different package card
  const handlePickPackage = async (serviceId) => {
    if (serviceId === pickedServiceId && packageDetail) return;

    const previousServiceId = pickedServiceId;
    setPickedServiceId(serviceId);

    // The recommended card's base detail came with the preview — reuse it unless
    // extras are selected (then the merged view must come from the backend).
    if (previewData?.recommended?.service_id === serviceId && selectedExtras.length === 0) {
      setPackageDetail(previewData.recommended);
      return;
    }

    const ok = await fetchPackageDetail(serviceId, selectedExtras);
    if (!ok) setPickedServiceId(previousServiceId);
  };

  const handleToggleExtra = async (serviceId) => {
    const previousExtras = selectedExtras;
    const next = selectedExtras.includes(serviceId)
      ? selectedExtras.filter(id => id !== serviceId)
      : [...selectedExtras, serviceId];
    setSelectedExtras(next);

    // Ticking an extra changes the parts and the total — refresh the merged preview
    if (pickedServiceId && packageDetail) {
      if (next.length === 0 && previewData?.recommended?.service_id === pickedServiceId) {
        setPackageDetail(previewData.recommended);
      } else {
        const ok = await fetchPackageDetail(pickedServiceId, next);
        if (!ok) setSelectedExtras(previousExtras);
      }
    }
  };

  const handleSwapPart = (part) => {
    setSwapPart(part);
    setSwapOpen(true);
  };

  const handleSwapSelect = (candidate) => {
    // Update the package detail with the chosen part source
    if (!packageDetail || !swapPart) return;

    const matchSourceBySource = {
      catalog: 'catalog_swap',
      catalogue: 'catalogue',
      supplier: 'supplier_swap',
      ezyvin: 'none',
    };

    const updatedParts = packageDetail.resolved_parts.map(p => {
      if (p.ezyvin_part_number === swapPart.ezyvin_part_number && p.ezyvin_part_name === swapPart.ezyvin_part_name) {
        return {
          ...p,
          status: 'ok',
          matched_part_id: candidate.part_id || null,
          matched_part_name: candidate.part_name,
          matched_part_price: candidate.sell_price || candidate.sales_price || null,
          price_missing: false,
          match_source: matchSourceBySource[candidate.source] || p.match_source,
          catalogue_candidate: candidate.source === 'catalogue' ? candidate.catalogue_item : p.catalogue_candidate,
          auto_picked: candidate.source === 'supplier' ? {
            article_code: candidate.article_code,
            brand: candidate.brand,
            part_name: candidate.part_name,
            purchase_price: candidate.purchase_price,
            sales_price: candidate.sell_price || candidate.sales_price,
            supplier_name: candidate.supplier_name,
            supplier_integration_id: candidate.supplier_integration_id ?? null,
            in_stock: candidate.in_stock,
          } : p.auto_picked,
        };
      }
      return p;
    });

    setPackageDetail({ ...packageDetail, resolved_parts: updatedParts });

    // Carry the explicit choice to the backend so attach-time re-resolution can never
    // silently pick something else than what the user chose.
    if (candidate.source === 'supplier' || candidate.source === 'ezyvin') {
      setSelectedVariants(prev => ({
        ...prev,
        [swapPart.ezyvin_part_name]: {
          part_number: candidate.article_code || '',
          part_name: candidate.part_name || '',
          supplier_name: candidate.supplier_name || candidate.brand || '',
          brand: candidate.brand || '',
          supplier_integration_id: candidate.supplier_integration_id ?? null,
          oe_number: candidate.variant?.oe_number || swapPart.oe_number || '',
          purchase_price: candidate.purchase_price ?? null,
          sales_price: candidate.sell_price || candidate.sales_price || null,
        },
      }));
      setSelectedCatalogueItems(prev => {
        const next = { ...prev };
        delete next[swapPart.ezyvin_part_name];
        return next;
      });
    } else if (candidate.source === 'catalogue') {
      setSelectedCatalogueItems(prev => ({
        ...prev,
        [swapPart.ezyvin_part_name]: candidate.catalogue_item,
      }));
      setSelectedVariants(prev => {
        const next = { ...prev };
        delete next[swapPart.ezyvin_part_name];
        return next;
      });
    } else {
      // Own-register part: override_part_id carries it; clear other overrides
      setSelectedVariants(prev => {
        const next = { ...prev };
        delete next[swapPart.ezyvin_part_name];
        return next;
      });
      setSelectedCatalogueItems(prev => {
        const next = { ...prev };
        delete next[swapPart.ezyvin_part_name];
        return next;
      });
    }

    setSwapOpen(false);
    setSwapPart(null);
  };

  const handleCollapse = () => {
    setPickedServiceId(null);
    setPackageDetail(null);
  };

  const handleJobSetupSaved = (jobId) => {
    setDefaultJobId(jobId);
    // Resume the fetch the setup dialog interrupted. triggerFetch would read the
    // not-yet-committed defaultJobId from state, so call the fetch directly.
    const identifier = vehicleId || plate;
    if (identifier && mileage && parseInt(mileage) > 0) {
      fetchPreview(identifier, mileage);
    }
  };

  // Build the summary for package cards
  const hourlyRate = currentOrganization?.default_labor_cost_per_hour || 75;
  const priceIncludesVat = currentOrganization?.default_price_includes_vat;
  const buildSummary = (svc) => {
    const laborExclVat = (svc?.service_time || 0) * hourlyRate;
    const partsExclVat = (svc?.resolved_parts || []).reduce((sum, p) => {
      const price = parseFloat(
        p.matched_part_price ||
        p.auto_picked?.sales_price ||
        p.auto_picked?.purchase_price ||
        p.catalogue_candidate?.purchase_price ||
        0
      );
      return sum + price * (p.quantity || 1);
    }, 0);
    const totalExclVat = laborExclVat + partsExclVat;
    return {
      service_id: svc?.service_id,
      // null, not 0 — time-based and named schedules carry no odometer figure and
      // must not render as "0 km".
      km: svc?.km || null,
      // On a repeating cycle `km` is the interval and this is the odometer reading it
      // falls due at — the two are not interchangeable and only this one is a mileage.
      occurrence_km: svc?.occurrence_km || null,
      occurrence_label: svc?.occurrence_label || '',
      service_text: svc?.service_text || '',
      description: svc?.description || '',
      // What the service is called when it has a name ("Huolto B", "Öljy- ja
      // tarkastushuolto yhdistettynä"); empty for a plain km rule.
      name: svc?.name || '',
      title: svc?.title || '',
      interval_label: svc?.interval_label || '',
      interval_kind: svc?.interval_kind || '',
      service_time: svc?.service_time,
      part_count: svc?.part_count || svc?.resolved_parts?.length || 0,
      estimated_total: priceIncludesVat ? calcPriceInclVat(totalExclVat) : totalExclVat,
    };
  };

  // A repeating cycle (25 000 km / 50 000 km, forever) and an enumerated ladder
  // (15 000, 30 000 … 300 000) both come from Ezyvin as "every N km", but they read
  // differently: a ladder rung IS an odometer milestone, while a cycle's number is the
  // interval and the odometer reading is when it next falls due. Labelling a cycle
  // like a ladder is what showed "50 000 km" for a car with 90 000 km on it.
  const isCycle = previewData?.schedule_shape === 'cycle';
  const formatKm = (km) => `${Number(km).toLocaleString('fi-FI')} km`;

  // One place decides what a card is headed by and what sits under it. A service
  // with a name is headed by the name (a Mercedes mechanic looks for "Huolto B",
  // not for the parts it happens to contain); a milestone by its reading; a cycle
  // service by its name or interval, dated by when it next falls due.
  const cardLabels = (summary, kind) => {
    const { name } = summary;
    // A named service's interval wording IS its name — never say it twice.
    const intervalLine = summary.interval_label && summary.interval_label !== name ? summary.interval_label : '';
    if (isCycle) {
      return {
        heading: name || summary.interval_label,
        subtitle: [name ? intervalLine : '', summary.occurrence_label].filter(Boolean).join(' · '),
      };
    }
    if (summary.km) {
      const subtitle = kind === 'recommended'
        ? t('oem.recommendedForKm', { km: Number(summary.km).toLocaleString('fi-FI') })
        : kind === 'previous'
          ? t('oem.previousInterval')
          : kind === 'next'
            ? t('oem.nextInterval')
            : summary.interval_label;
      return { heading: formatKm(summary.km), subtitle };
    }
    // Named or time-only service: the name is the heading, so the line under it
    // must say something else — the interval, or failing that what it contains.
    const heading = name || summary.title || summary.interval_label;
    let subtitle = name ? intervalLine : summary.interval_label;
    if (!subtitle || subtitle === heading) subtitle = summary.description !== heading ? summary.description : '';
    return { heading, subtitle };
  };
  const card = (summary, kind) => ({ ...summary, ...cardLabels(summary, kind) });

  // The manufacturer's base package can be a near-empty shell (BMW: "Standard
  // workshop operations", 0.2 h, no parts) with the real work — the oil change —
  // listed as extras the car itself calls for. Those must not hide behind a
  // collapsed accordion.
  const thinRecommended = !!previewData?.recommended
    && (previewData.recommended.resolved_parts || []).length === 0;
  const extrasOpen = thinRecommended || previewData?.schedule_shape === 'named';

  // Title of the expanded preview: the service's name and the reading it is done at.
  const previewHeading = () => {
    if (!packageDetail) return '';
    const pointKm = isCycle ? packageDetail.occurrence_km || packageDetail.km : packageDetail.km;
    return [packageDetail.name, pointKm ? formatKm(pointKm) : ''].filter(Boolean).join(' · ');
  };

  const recommendedSummary = previewData?.recommended ? buildSummary(previewData.recommended) : null;
  const previousSummary = previewData?.previous ? buildSummary(previewData.previous) : null;
  const nextSummary = previewData?.next ? buildSummary(previewData.next) : null;
  // Schedules with no km ladder offer their distinct services side by side instead; so
  // does a cycle, where the choice is between service types rather than milestones.
  const alternativeSummaries = (previewData?.alternatives || []).map(buildSummary);

  // Dialog sizing
  const collapsedWidth = 540;
  const expandedWidth = isDesktop ? 1100 : 540;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullScreen={!isDesktop && expanded}
        PaperProps={{
          sx: {
            // fullScreen mode: the sx class outranks .MuiDialog-paperFullScreen, so
            // fixed width/height here would silently defeat it on mobile.
            ...((!isDesktop && expanded)
              ? { width: '100%', maxWidth: '100%', maxHeight: '100%', borderRadius: 0 }
              : {
                  width: expanded ? expandedWidth : collapsedWidth,
                  maxWidth: '95vw',
                  maxHeight: expanded ? '90vh' : '85vh',
                  borderRadius: '16px',
                }),
            boxShadow: '0 20px 60px rgba(0,0,0,0.30)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            transition: theme.transitions.create(['width'], {
              duration: 360,
              easing: 'cubic-bezier(0.4,0,0.2,1)',
            }),
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: '18px 22px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            {expanded && (
              <IconButton onClick={handleCollapse} size="small" sx={{ color: 'text.secondary' }}>
                <ArrowBackOutlined />
              </IconButton>
            )}
            <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
              {expanded
                ? [t('oem.previewTitle'), previewHeading()].filter(Boolean).join(' · ')
                : t('oem.dialogTitle')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseOutlined />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: expanded && isDesktop ? '1fr 1fr' : '1fr',
            gridTemplateRows: expanded && !isDesktop ? 'auto 1fr' : '1fr',
            transition: theme.transitions.create(['grid-template-columns', 'grid-template-rows'], {
              duration: 360,
              easing: 'cubic-bezier(0.4,0,0.2,1)',
            }),
          }}
        >
          {/* LEFT — form + interval picker */}
          <Box
            sx={{
              overflowY: 'auto',
              p: '0 22px 18px',
              borderRight: expanded && isDesktop ? 1 : 0,
              borderBottom: expanded && !isDesktop ? 1 : 0,
              borderColor: 'divider',
            }}
          >
            {/* Form fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              <TextField
                label={t('oem.registrationNumber')}
                value={plate}
                onChange={(e) => handlePlateChange(e.target.value)}
                required
                fullWidth
                size="small"
                disabled={mode === 'augment' || mode === 'picker' || (mode === 'create' && !!propVehicle)}
                inputProps={{ style: { ...plateFont, fontWeight: 600 } }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              <TextField
                label={t('oem.mileage')}
                value={mileage}
                onChange={(e) => handleMileageChange(e.target.value)}
                onBlur={() => { if (plate.trim().length >= 3 && mileage && parseInt(mileage) > 0 && fetchStatus === 'idle') triggerFetch(); }}
                // Same idle guard as onBlur above: a preview is a paid Ezyvin call, and
                // Enter pressed twice used to buy the same schedule twice.
                onKeyDown={(e) => { if (e.key === 'Enter' && plate.trim().length >= 3 && mileage && parseInt(mileage) > 0 && fetchStatus === 'idle') { e.preventDefault(); triggerFetch(); } }}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: <InputAdornment position="end">km</InputAdornment>,
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              {plate.trim().length >= 3 && mileage && parseInt(mileage) > 0 && fetchStatus === 'idle' && (
                <Button
                  variant="contained"
                  onClick={triggerFetch}
                  sx={{ mt: 0.5, borderRadius: '10px', textTransform: 'none', bgcolor: 'secondary.main' }}
                >
                  {t('oem.searchPackages', { defaultValue: 'Hae OEM-huoltopaketit' })}
                </Button>
              )}
            </Box>

            {/* Vehicle info */}
            {vehicleInfo && (
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1, ml: 0.5 }}>
                {vehicleInfo.manufacturer} {vehicleInfo.model} {vehicleInfo.year_model && `(${vehicleInfo.year_model})`}
                {vehicleInfo.engine_code && ` · ${vehicleInfo.engine_code}`}
              </Typography>
            )}

            {/* Loading */}
            {fetchStatus === 'loading' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 4 }}>
                <CircularProgress size={28} />
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', textAlign: 'center', px: 2 }}>
                  {t('oem.fetchTakesMinutes')}
                </Typography>
              </Box>
            )}

            {/* Error */}
            {fetchStatus === 'error' && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>
                {fetchError}
              </Alert>
            )}

            {/* OEM suggestion panel */}
            {fetchStatus === 'success' && previewData && (
              <Box sx={{ mt: 2.25, border: 1, borderColor: 'divider', borderRadius: '12px', overflow: 'hidden' }}>
                {/* Header strip */}
                <Box
                  sx={{
                    p: '12px 16px',
                    background: 'linear-gradient(180deg, #f5f9ff 0%, #ffffff 100%)',
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                  }}
                >
                  <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: 'rgba(49,130,206,0.10)', color: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AutoAwesomeOutlined sx={{ fontSize: 16 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t('oem.oemPackages')}</Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {previewData.vehicle?.manufacturer} {previewData.vehicle?.model} {previewData.vehicle?.year_model}
                      {previewData.vehicle?.engine_code && ` · ${previewData.vehicle.engine_code}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11, color: 'text.secondary' }}>
                    <CheckCircleOutlined sx={{ fontSize: 14, color: '#2f8c54' }} />
                    {t('oem.fetched')}
                  </Box>
                </Box>

                {/* Package cards */}
                <Box sx={{ p: 1.75, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {previewData?.schedule_ended && (
                    <MuiAlert severity="warning" sx={{ borderRadius: '8px', fontSize: 12 }}>
                      {t('oem.scheduleEnded')}
                    </MuiAlert>
                  )}
                  {recommendedSummary && (
                    <OemPackageCardRecommended
                      pkg={card(recommendedSummary, 'recommended')}
                      picked={pickedServiceId === recommendedSummary.service_id}
                      onPick={() => handlePickPackage(recommendedSummary.service_id)}
                    />
                  )}

                  {(previousSummary || nextSummary || alternativeSummaries.length > 0) && (
                    <>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', pt: 0.75, px: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                        {isCycle && t('oem.otherServiceTypes')}
                        {!isCycle && (previewData?.mileage_based === false
                          ? t('oem.otherServices')
                          : t('oem.otherIntervals'))}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                        {previousSummary && (
                          <OemPackageCardSmall
                            pkg={card(previousSummary, 'previous')}
                            picked={pickedServiceId === previousSummary.service_id}
                            onPick={() => handlePickPackage(previousSummary.service_id)}
                          />
                        )}
                        {nextSummary && (
                          <OemPackageCardSmall
                            pkg={card(nextSummary, 'next')}
                            picked={pickedServiceId === nextSummary.service_id}
                            onPick={() => handlePickPackage(nextSummary.service_id)}
                          />
                        )}
                        {alternativeSummaries.map((alt) => (
                          <OemPackageCardSmall
                            key={alt.service_id}
                            pkg={card(alt, 'alternative')}
                            picked={pickedServiceId === alt.service_id}
                            onPick={() => handlePickPackage(alt.service_id)}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>

                {/* Extras */}
                {(previewData.extras?.length > 0 || previewData.upcoming_extras?.length > 0) && (
                  <Box sx={{ px: 1.75, pb: 1.75 }}>
                    <LisapalveluSection
                      extras={previewData.extras}
                      upcomingExtras={previewData.upcoming_extras}
                      selectedExtras={selectedExtras}
                      onToggleExtra={handleToggleExtra}
                      defaultOpen={extrasOpen}
                      hint={thinRecommended ? t('oem.thinRecommendedHint') : ''}
                    />
                  </Box>
                )}
              </Box>
            )}

            {/* Hint */}
            {!expanded && fetchStatus === 'success' && (
              <Box sx={{ mt: 1.75, display: 'flex', gap: 1, alignItems: 'flex-start', p: '10px 14px', bgcolor: '#f8fafc', borderRadius: '8px' }}>
                <LightbulbOutlined sx={{ fontSize: 16, color: '#f6ad55', mt: 0.125 }} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                  {t('oem.clickHint')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* RIGHT — preview pane */}
          {expanded && (
            <Box
              sx={{
                overflowY: 'auto',
                p: '18px 22px',
                bgcolor: '#f8fafc',
                '@keyframes oemFadeIn': {
                  from: { opacity: 0, transform: isDesktop ? 'translateX(12px)' : 'translateY(12px)' },
                  to: { opacity: 1, transform: 'translate(0)' },
                },
                animation: 'oemFadeIn 400ms cubic-bezier(0.4,0,0.2,1) 80ms both',
              }}
            >
              {packageLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : packageDetail ? (
                <OemPreviewPane
                  packageData={packageDetail}
                  hourlyRate={currentOrganization?.default_labor_cost_per_hour || 75}
                  priceIncludesVat={currentOrganization?.default_price_includes_vat}
                  onSwapPart={handleSwapPart}
                  selectedVariants={selectedVariants}
                  lastUpdated={lastUpdated}
                />
              ) : null}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: '14px 22px', borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.25, flexShrink: 0, flexWrap: 'wrap' }}>
          {/* Price transparency: what confirming this package costs the workshop */}
          {expanded && (
            <Typography sx={{ flex: 1, minWidth: 180, fontSize: 12, color: 'text.secondary', textAlign: 'left' }}>
              {previewData?.already_charged
                ? t('oem.noChargeNote')
                : previewData?.price_eur
                  ? t('oem.chargeNote', { price: formatPriceWithCurrency(previewData.price_eur) })
                  : ''}
            </Typography>
          )}
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{ borderRadius: '10px', px: 3, textTransform: 'none', borderColor: 'divider', color: 'text.primary' }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={!expanded || creating}
            startIcon={expanded ? (creating ? <CircularProgress size={16} color="inherit" /> : <CheckOutlined />) : null}
            endIcon={!expanded ? <ArrowForwardOutlined /> : null}
            onClick={async () => {
              if (!expanded && pickedServiceId) {
                handlePickPackage(pickedServiceId);
                return;
              }
              if (!expanded) return;

              const partsPayload = (packageDetail?.resolved_parts || []).map(rp => ({
                ezyvin_part_name: rp.ezyvin_part_name,
                ezyvin_part_number: rp.ezyvin_part_number,
                override_part_id: rp.matched_part_id || null,
                quantity: rp.quantity || 1,
                selected_variant: selectedVariants[rp.ezyvin_part_name] || null,
                selected_catalogue_item: selectedCatalogueItems[rp.ezyvin_part_name] || null,
              }));

              if (mode === 'picker') {
                // No backend call — hand the selection to the hosting creation dialog
                onPackageSelected?.({
                  service_id: pickedServiceId,
                  extra_service_ids: selectedExtras,
                  mileage: parseInt(mileage) || 0,
                  parts: partsPayload,
                  summary: {
                    // The reading the service is done at — on a cycle that is the
                    // service point, not the interval.
                    km: (isCycle ? packageDetail?.occurrence_km : null) ?? packageDetail?.km ?? null,
                    name: packageDetail?.name || '',
                    service_text: packageDetail?.service_text || '',
                    parts_count: (packageDetail?.resolved_parts || []).length,
                    extras_count: selectedExtras.length,
                  },
                });
                handleClose();
                return;
              }

              setCreating(true);
              try {
                const payload = {
                  vehicle_id: vehicleId,
                  customer_id: customerId || vehicleInfo?.last_customer_id || null,
                  service_id: pickedServiceId,
                  extra_service_ids: selectedExtras,
                  mileage: parseInt(mileage) || 0,
                  parts: partsPayload,
                  organization: orgId,
                };

                if (mode === 'augment') {
                  // Attach to the open work order — never create a new one
                  await axios.post(`/api/oem/augment/${maintenanceId}/`, payload);
                  onAttached?.();
                  handleClose();
                  return;
                }

                const resp = await axios.post('/api/oem/from-oem/', payload);
                const createdId = resp.data.id;

                addCreatedMaintenance?.(resp.data);
                handleClose();
                navigate(`/maintenances/${createdId}`);
              } catch (err) {
                const msg = err.response?.data?.error || t('oem.fetchError');
                setSnackbar({ open: true, message: msg, severity: 'error' });
              } finally {
                setCreating(false);
              }
            }}
            sx={{
              borderRadius: '10px',
              px: 3,
              textTransform: 'none',
              bgcolor: 'secondary.main',
              boxShadow: '0 4px 12px rgba(49, 130, 206, 0.3)',
              '&:hover': { bgcolor: '#4299e1' },
              '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#fff' },
            }}
          >
            {expanded
              ? (mode === 'augment'
                ? t('oem.addToWorkOrder')
                : mode === 'picker'
                  ? t('oem.selectPackage')
                  : t('oem.createWorkOrder'))
              : t('oem.previewButton')}
          </Button>
        </Box>
      </Dialog>

      {/* Part swap modal */}
      <PartSwapModal
        open={swapOpen}
        onClose={() => { setSwapOpen(false); setSwapPart(null); }}
        part={swapPart}
        onSelect={handleSwapSelect}
        organizationId={orgId}
      />

      {/* First-time job setup */}
      <OemJobSetupDialog
        open={jobSetupOpen}
        onClose={() => setJobSetupOpen(false)}
        onSaved={handleJobSetupSaved}
        organizationId={orgId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
}
